import { useTranslation } from 'react-i18next';
import {
	Button,
	Divider,
	Grid,
	Modal,
	Select,
	Typography,
	Upload,
	UploadFile,
	UploadProps,
} from 'antd';
import {
	DownloadOutlined,
	InboxOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useCallback, useState } from 'react';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useFunctionariesList } from './useFunctionariesList';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';

interface BulkUploadFunctionariesProps {
	open: boolean;
	onClose: () => void;
}

const { Dragger } = Upload;
const { Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

export const BulkUploadFunctionaries = ({
	open,
	onClose,
}: BulkUploadFunctionariesProps) => {
	const { t, i18n } = useTranslation();
	const { showToastMessage } = useToastMessage();
	const isLengthyLanguage = ['ta', 'ma', 'te', 'ka'].includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
	const [fileList, setFileList] = useState<UploadFile<Blob>[]>([]);
	const [roleSelected, setRoleSelected] = useState(true);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [uploadLoading, setUploadLoading] = useState(false);

	const { downloadTemplateForBulkFunctionaries, bulkUploadDataFunctionaries } =
		useFunctionariesList();

	const functionaryTypeDropdown = [
		{ label: t('careTeam'), value: UserRole.CARE_TEAM },
		{ label: t('approver'), value: UserRole.APPROVER },
		{ label: t('preworkAdmin'), value: UserRole.PREWORK_ADMIN },
		{ label: t('screeningAdmin'), value: UserRole.SCREENING_ADMIN },
		{ label: t('zc'), value: UserRole.ZC },
		{ label: t('proposer'), value: UserRole.FUNCTIONARY },
	];

	const props: UploadProps = {
		multiple: false,
		accept:
			'.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		onRemove: (file: UploadFile<Blob>) => {
			const index = fileList.indexOf(file);
			const newFileList = fileList.slice();
			newFileList.splice(index, 1);
			setFileList(newFileList);
		},
		beforeUpload: (file) => {
			if (
				file.type !==
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			) {
				showToastMessage('error', t('onlyExcelOrCsvFiles'));
				return Upload.LIST_IGNORE;
			}

			setFileList([file]);
			return false;
		},
		fileList,
	};

	const onRoleChange = useCallback((roleVal: string) => {
		setRoleFilter(roleVal);
		setRoleSelected(false);
	}, []);

	const onDownloadClick = useCallback(async () => {
		if (roleFilter) {
			await downloadTemplateForBulkFunctionaries(roleFilter);
		}
	}, [roleFilter, downloadTemplateForBulkFunctionaries]);

	const handleDownloadClick = useCallback(() => {
		setDownloadLoading(true);
		onDownloadClick()
			.catch((error: Error) => {
				showToastMessage('error', error.message);
			})
			.finally(() => {
				setDownloadLoading(false);
			});
	}, [onDownloadClick, showToastMessage]);

	const onUploadClick = useCallback(async () => {
		const { name: fileName, type: fileType } = fileList[0];
		const dotIndex = fileName.lastIndexOf('.');
		const csvFileName =
			dotIndex !== -1 ? fileName.slice(0, dotIndex) : fileName;
		const fileExtension =
			dotIndex !== -1
				? fileName.slice(dotIndex + 1)
				: fileType?.split('/').pop();

		const fileToUpload = [
			{
				fileName: csvFileName || '',
				fileExtension: fileExtension || '',
				fileType: fileType || '',
			},
		];

		if (fileToUpload.length && roleFilter) {
			await bulkUploadDataFunctionaries(
				fileToUpload,
				fileList[0],
				fileExtension,
				roleFilter
			);
		}
	}, [roleFilter, bulkUploadDataFunctionaries, fileList]);

	const handleUploadClick = useCallback(() => {
		setUploadLoading(true);
		onUploadClick()
			.then(() => {
				setRoleFilter('');
				setFileList([]);
				onClose();
			})
			.catch((error: Error) => {
				showToastMessage('error', error.message);
			})
			.finally(() => {
				setUploadLoading(false);
			});
	}, [onUploadClick, showToastMessage, onClose]);

	return (
		<div className="flex flex-col h-full min-h-0 p-[8px]">
			<Modal
				open={open}
				onCancel={onClose}
				footer={null}
				centered
				className={
					isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
				}
				closable={true}
				maskClosable={true}
			>
				<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
					{t('bulkUpload')}
				</div>
				<Divider className="h-0.5 bg-gray-400" />
				<div className="flex flex-col items-center justify-center md:flex-row gap-4">
					<div className="mb-[8px] flex flex-col gap-2 w-full md:w-1/2">
						<Text
							className={`block text-xs ${
								isMobile ? 'text-left' : 'text-center'
							} font-bold text-gray-650 pb-[8px]`}
						>
							{t('selectRoleToBulkUpload')}
						</Text>
						<Select
							showSearch
							defaultActiveFirstOption={false}
							showArrow={false}
							filterOption={false}
							onChange={onRoleChange}
							value={roleFilter}
						>
							{functionaryTypeDropdown.map((option) => (
								<Option key={option.value} value={option.value}>
									{option.label}
								</Option>
							))}
						</Select>
					</div>
				</div>

				<Divider className="h-0.5 bg-gray-400" />
				<h3 className="text-center">{t('downloadTemplate')}</h3>
				<div className="flex items-center justify-center">
					<Button
						className="w-52 text-xs font-semibold "
						size="large"
						icon={<DownloadOutlined />}
						onClick={handleDownloadClick}
						loading={downloadLoading}
						disabled={roleSelected}
					>
						{t('download')}
					</Button>
				</div>
				<Divider className="mt-8 h-0.5 bg-gray-400" />
				<h3 className="text-center">{t('uploadFunctionaries')}</h3>
				<div className="flex flex-col items-center justify-center">
					<Dragger className="m-4" {...props} disabled={roleSelected}>
						<p className="ant-upload-drag-icon">
							<InboxOutlined className="!text-hover-black" />
						</p>
						<p className="ant-upload-text">{t('clickOrDragToUpload')}</p>
						<p className="ant-upload-hint">{t('uploadHint')}</p>
					</Dragger>
					<Button
						className="w-52 text-xs font-semibold "
						size="large"
						disabled={fileList.length === 0 || roleSelected}
						icon={<UploadOutlined />}
						onClick={handleUploadClick}
						loading={uploadLoading}
					>
						{t('upload')}
					</Button>
				</div>
			</Modal>
		</div>
	);
};
